import heroVideo from '@/assets/media/hero-silent.mp4'
import heroPoster from '@/assets/media/hero-poster.webp'

const ENTRY_KEY = 'jiyu-explored-at-v1'
const ENTRY_TTL = 30 * 60 * 1000

function hasDirectDestination() {
  return typeof window !== 'undefined' && !['', '#', '#home'].includes(window.location.hash)
}

function shouldSkipEntry() {
  if (hasDirectDestination()) return true
  try {
    const enteredAt = Number(window.localStorage.getItem(ENTRY_KEY))
    const age = Date.now() - enteredAt
    return enteredAt > 0 && age >= 0 && age < ENTRY_TTL
  } catch { return false }
}

export default {
  data() {
    return {
      entryPhase: shouldSkipEntry() ? 'ready' : 'gate', entryAnimated: false,
      entryTimer: null, entryKeyboard: false, entryDisposed: false,
      heroVideoUrl: heroVideo, heroPosterUrl: heroPoster,
      heroSourceEnabled: false, heroVideoReady: false, heroVideoFailed: false,
      heroVisible: false, heroObserver: null, entryMotion: null, entryReducedMotion: false
    }
  },
  computed: {
    contentReady() { return this.entryPhase !== 'gate' },
    entryClasses() {
      return { 'intro-gated': this.entryPhase === 'gate', 'intro-animated': this.entryAnimated,
        'intro-skipped': this.entryPhase === 'ready' && !this.entryAnimated }
    }
  },
  mounted() {
    this.entryMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.entryReducedMotion = this.entryMotion.matches
    this.entryMotion.addEventListener('change', this.onEntryMotionChange)
    document.addEventListener('visibilitychange', this.syncHeroPlayback)
    window.addEventListener('pagehide', this.pauseHeroVideo)
    window.addEventListener('pageshow', this.syncHeroPlayback)
    window.addEventListener('hashchange', this.onEntryHashChange)
    if (this.entryPhase === 'gate') {
      document.body.classList.add('intro-locked')
      window.scrollTo({ top: 0, behavior: 'instant' })
    }
    const bounds = this.$refs.hero.getBoundingClientRect()
    this.heroVisible = bounds.bottom > 0 && bounds.top < window.innerHeight
    this.heroObserver = new IntersectionObserver(([entry]) => {
      this.heroVisible = entry.isIntersecting
      this.syncHeroPlayback()
    }, { threshold: 0 })
    this.heroObserver.observe(this.$refs.hero)
    this.syncHeroPlayback()
    if (hasDirectDestination()) this.$nextTick(() => {
      if (!this.entryDisposed) document.getElementById(window.location.hash.slice(1))?.scrollIntoView()
    })
  },
  beforeUnmount() {
    this.entryDisposed = true
    window.clearTimeout(this.entryTimer)
    this.heroObserver?.disconnect()
    this.entryMotion?.removeEventListener('change', this.onEntryMotionChange)
    document.removeEventListener('visibilitychange', this.syncHeroPlayback)
    window.removeEventListener('pagehide', this.pauseHeroVideo)
    window.removeEventListener('pageshow', this.syncHeroPlayback)
    window.removeEventListener('hashchange', this.onEntryHashChange)
    this.pauseHeroVideo()
    document.body.classList.remove('intro-locked')
  },
  methods: {
    beginExplore(event) {
      if (this.entryPhase !== 'gate') return
      this.entryKeyboard = event?.detail === 0
      this.entryAnimated = !this.entryReducedMotion
      this.entryPhase = 'opening'
      try { window.localStorage.setItem(ENTRY_KEY, String(Date.now())) } catch { /* Entry works without storage. */ }
      // Never await media loading. A failed or blocked video leaves the poster.
      this.syncHeroPlayback()
      this.$nextTick(() => {
        if (this.entryDisposed) return
        this.observeSections()
        if (this.entryReducedMotion) this.finishEntry()
        else this.entryTimer = window.setTimeout(this.finishEntry, 1500)
      })
    },
    finishEntry() {
      if (this.entryDisposed) return
      window.clearTimeout(this.entryTimer)
      this.entryTimer = null
      this.entryPhase = 'ready'
      document.body.classList.remove('intro-locked')
      if (this.entryKeyboard) this.$nextTick(() => {
        if (!this.entryDisposed && this.entryPhase === 'ready') this.$refs.heroHeading?.focus({ preventScroll: true })
      })
    },
    onEntryHashChange() {
      if (this.entryPhase === 'ready' || !hasDirectDestination()) return
      this.entryAnimated = false
      this.finishEntry()
      this.$nextTick(() => {
        if (this.entryDisposed) return
        this.observeSections()
        document.getElementById(window.location.hash.slice(1))?.scrollIntoView()
      })
    },
    onEntryMotionChange(event) {
      this.entryReducedMotion = event.matches
      if (event.matches && this.entryPhase === 'opening') this.finishEntry()
      this.syncHeroPlayback()
    },
    canPlayHero() {
      return !this.entryDisposed && this.heroVisible && !document.hidden && !this.entryReducedMotion && !this.heroVideoFailed
    },
    syncHeroPlayback() {
      if (this.entryDisposed) return
      if (!this.canPlayHero()) { this.pauseHeroVideo(); return }
      if (!this.heroSourceEnabled) {
        this.heroSourceEnabled = true
        this.$nextTick(this.syncHeroPlayback)
        return
      }
      const video = this.$refs.heroVideo
      if (!video || !video.paused) return
      video.muted = true
      video.play()?.then(() => {
        if (!this.canPlayHero()) video.pause()
      }).catch(() => { /* Autoplay can be blocked; the Explore click retries it. */ })
    },
    pauseHeroVideo() { this.$refs.heroVideo?.pause() },
    onHeroPlaying() {
      if (!this.canPlayHero()) { this.pauseHeroVideo(); return }
      this.heroVideoReady = true
    },
    onHeroVideoError() {
      this.heroVideoFailed = true
      this.heroVideoReady = false
      this.pauseHeroVideo()
    }
  }
}
